import React, { useEffect } from "react";
import { ApplicantProfileIdentityUpload } from "./applicant-profile-identity-upload";
import { useLazyQuery, useMutation } from "@apollo/client";
import { UploadFile } from "antd";
import {  ApplicantUserNameOnDocumentAsset, FileInfo, StaffApplicantProfileIdentityDocumentUploadContainerApplicantUserFileCreateAuthHeaderDocument, StaffApplicantProfileIdentityDocumentUploadContainerApplicantUserFilesDocument, StaffApplicantProfileIdentityDocumentUploadContainerApplicantUserUpdateDocument } from "../../../../generated";
import { ComponentQueryLoader } from "../../../shared/component-query-loader";
import { AuthResult, AzureBlobResponse } from "../../../shared/file-upload/azure-upload";

interface ApplicantProfileIdentityUploadContainerProps {
  applicantId: string;
}

export const ApplicantProfileIdentityUploadContainer: React.FC<ApplicantProfileIdentityUploadContainerProps> = ({ applicantId }) => {
  const [isUploadProcessing, setIsUploadProcessing] = React.useState(false);
  const [updateAssets] = useMutation(
    StaffApplicantProfileIdentityDocumentUploadContainerApplicantUserUpdateDocument
  );
  const [createNewAssetHeader] = useMutation(
    StaffApplicantProfileIdentityDocumentUploadContainerApplicantUserFileCreateAuthHeaderDocument
  );
  const [getFilesData, { data: filesData }] = useLazyQuery(
    StaffApplicantProfileIdentityDocumentUploadContainerApplicantUserFilesDocument,
    {
      variables: {
        applicantUserId: applicantId,
      },
      fetchPolicy: 'network-only',
    }
  );

  const fetchFilesData = () => {
    getFilesData({
      variables: {
        applicantUserId: applicantId
      },
      fetchPolicy: 'network-only'
    });
    setIsUploadProcessing(false);
  };

  useEffect(() => {
    fetchFilesData();
  }, []);

  const addNewAsset = async (file: File): Promise<AuthResult> => {
    setIsUploadProcessing(true);
    return createNewAssetHeader({
      variables: {
        input: {
          applicantId: applicantId,
          fileName: file.name,
          contentType: file.type,
          contentLength: file.size,
        },
      },
    })
      .then((response) => {
        return {
          ...response?.data?.applicantUserFileCreateAuthHeader,
          isAuthorized: true,
        } as AuthResult;
      })
      .catch((error) => {
        console.error("Error uploading asset: ", error);
        return { isAuthorized: false } as AuthResult;
      });
  };

  const onAddAsset = async (response: AzureBlobResponse, _file: UploadFile) => {
    const filename = response.blobUrl.split('/').pop() ?? '';
    const existing = filesData?.applicantUser?.personalInformation?.nameOnDocumentAssets?.map(
      (doc: any) => ({
        id: doc.id,
        documentName: doc.documentName,
        isDeleteRequested: doc.isDeleteRequested,
      })
    ) ?? [];
    const exists = existing.some(doc => doc.documentName === filename);

    const updatedFormattedData = exists
      ? existing
      : [
          ...existing,
          {
            id: undefined,
            documentName: filename,
            isDeleteRequested: false,
          },
        ];

    await updateAssets({
      variables: {
        input: {
          id: applicantId,
          applicantUserDetails: {
            personalInformation: {
              nameOnDocumentAssets: updatedFormattedData,
            },
          },
        },
      },
    });
    fetchFilesData();
  };

  return (
    <ComponentQueryLoader
      loading={false}
      hasData={true}
      hasDataComponent={
        <ApplicantProfileIdentityUpload
        isUploadProcessing={isUploadProcessing}
          nameOnDocumentAssets={filesData?.applicantUser?.personalInformation?.nameOnDocumentAssets as ApplicantUserNameOnDocumentAsset[]}
          assetUpdateFunction={addNewAsset}
          onAssetUpdate={onAddAsset}
          files={filesData?.applicantUser?.files as FileInfo[]}
        />
      }
    />
  );
};
